import React from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronDown, Search, Star, UserPlus, X } from 'lucide-react';
import type { TicketAssignee } from '../../../types';
import Avatar from '../../shared/Avatar';

/**
 * MULTI-ASSIGNEE control for a ticket — "Assigned To" is 0..N team members, not
 * a single owner.
 *
 * Renders an overlapping avatar stack with a +N overflow badge, and opens a
 * searchable multi-select popover where agents are toggled on and off. Toggling
 * does NOT close the popover, so several people can be added or removed in one
 * visit — the whole point of the field.
 *
 * The popover is portalled to <body> because the right-hand context panel
 * scrolls and would otherwise clip it.
 */

export interface AssigneeAgent {
  uid: string;
  name?: string;
  email?: string;
}

export interface AssigneeStackProps {
  assignees: TicketAssignee[];
  agents: AssigneeAgent[];
  onChange: (next: TicketAssignee[]) => void;
  disabled?: boolean;
  /** Avatars shown before collapsing into a +N badge. */
  maxVisible?: number;
  size?: 'sm' | 'md';
}

/** Read a ticket's assignees, upgrading the legacy single-owner shape on read. */
export function ticketAssigneesOf(ticket: {
  assignees?: TicketAssignee[] | null;
  assignedTo?: string | null;
  assignedToName?: string | null;
} | null | undefined): TicketAssignee[] {
  const list = Array.isArray(ticket?.assignees)
    ? ticket!.assignees.filter(a => a && typeof a.uid === 'string' && a.uid)
    : [];
  if (list.length > 0) return list;
  // Legacy ticket written before multi-assignee existed.
  if (ticket?.assignedTo) {
    return [{ uid: ticket.assignedTo, name: ticket.assignedToName || 'Assigned', role: 'owner' }];
  }
  return [];
}

/** Exactly one owner: the first in the list. Mirrors the server rule. */
export function reindexOwner(list: TicketAssignee[]): TicketAssignee[] {
  return list.map((a, i) => ({ ...a, role: i === 0 ? 'owner' : 'collaborator' }));
}

function displayName(agent: { uid: string; name?: string; email?: string }): string {
  return agent.name || agent.email || agent.uid;
}

export default function AssigneeStack({
  assignees, agents, onChange, disabled = false, maxVisible = 3, size = 'sm',
}: AssigneeStackProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  const anchorRef = React.useRef<HTMLButtonElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);

  const syncRect = React.useCallback(() => {
    const el = anchorRef.current;
    if (el) setRect(el.getBoundingClientRect());
  }, []);

  React.useEffect(() => {
    if (!open) return;
    syncRect();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (anchorRef.current?.contains(t as Node) || popoverRef.current?.contains(t as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('resize', syncRect);
    window.addEventListener('scroll', syncRect, true);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', syncRect);
      window.removeEventListener('scroll', syncRect, true);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, syncRect]);

  const assignedUids = new Set(assignees.map(a => a.uid));

  const toggle = (agent: AssigneeAgent) => {
    if (disabled) return;
    const next = assignedUids.has(agent.uid)
      ? assignees.filter(a => a.uid !== agent.uid)
      : [...assignees, {
        uid: agent.uid,
        name: displayName(agent),
        role: 'collaborator' as const,
        assignedAt: new Date().toISOString(),
      }];
    // Instant apply — the stack updates and the popover stays open.
    onChange(reindexOwner(next));
  };

  const filteredAgents = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(a =>
      displayName(a).toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q),
    );
  }, [agents, query]);

  const visible = assignees.slice(0, maxVisible);
  const overflow = assignees.length - visible.length;
  const avatarClass = size === 'sm' ? 'h-6 w-6 text-[11px]' : 'h-8 w-8 text-[11px]';

  return (
    <div className="min-w-0">
      <button
        ref={anchorRef}
        type="button"
        onClick={() => { if (!disabled) { setOpen(o => !o); syncRect(); } }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={assignees.length === 0
          ? 'Assign team members to this ticket'
          : `Assigned to ${assignees.map(a => a.name).join(', ')} — change assignees`}
        title={assignees.length === 0
          ? 'Assign team members'
          : `${assignees.length} assigned — click to add or remove`}
        className={`group flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-all ${
          disabled
            ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-70'
            : 'cursor-pointer border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
        }`}
      >
        {assignees.length === 0 ? (
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
            <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
            Unassigned — add team members
          </span>
        ) : (
          <span className="flex min-w-0 items-center">
            <span className="flex items-center">
              {visible.map((a, i) => (
                <span
                  key={a.uid}
                  className={`${i > 0 ? '-ml-2' : ''} inline-flex rounded-full ring-2 ring-white`}
                  title={`${a.name}${a.role === 'owner' ? ' (owner)' : ''}`}
                >
                  <Avatar name={a.name} size="xs" className={avatarClass} />
                </span>
              ))}
            </span>
            {overflow > 0 && (
              <span
                className={`-ml-2 inline-flex items-center justify-center rounded-full bg-slate-200 font-extrabold text-slate-600 ring-2 ring-white ${avatarClass}`}
                title={assignees.slice(maxVisible).map(a => a.name).join(', ')}
              >
                +{overflow}
              </span>
            )}
            <span className="ml-2 truncate text-[11px] font-bold text-slate-600">
              {assignees.length === 1 ? assignees[0].name : `${assignees.length} assigned`}
            </span>
          </span>
        )}
        <ChevronDown
          className={`ml-auto h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && rect && ReactDOM.createPortal(
        <div
          ref={popoverRef}
          role="listbox"
          aria-multiselectable="true"
          aria-label="Assign team members"
          className="fixed z-[9200] w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
          style={{
            top: Math.max(8, Math.min(rect.bottom + 4, window.innerHeight - 320)),
            left: Math.max(8, Math.min(rect.left, window.innerWidth - 296)),
          }}
        >
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
              Assigned · {assignees.length}
            </span>
            {assignees.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold text-slate-400 transition-colors hover:bg-slate-100 hover:text-rose-600 cursor-pointer"
                aria-label="Unassign everyone"
              >
                <X className="h-3 w-3" aria-hidden="true" /> Clear all
              </button>
            )}
          </div>

          <div className="border-b border-slate-100 px-3 py-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search agents and admins..."
                aria-label="Search agents and admins"
                className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-7 pr-2 text-xs font-semibold outline-none focus:border-blue-400"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {filteredAgents.length === 0 && (
              <p className="px-3 py-3 text-center text-[11px] font-semibold text-slate-400">
                No agents match “{query}”.
              </p>
            )}
            {filteredAgents.map(agent => {
              const isAssigned = assignedUids.has(agent.uid);
              const record = assignees.find(a => a.uid === agent.uid);
              return (
                <button
                  key={agent.uid}
                  type="button"
                  role="option"
                  aria-selected={isAssigned}
                  onClick={() => toggle(agent)}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors cursor-pointer ${
                    isAssigned ? 'bg-blue-50/70' : 'hover:bg-slate-50'
                  }`}
                >
                  <Avatar name={displayName(agent)} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1">
                      <span className="truncate text-xs font-bold text-slate-700">{displayName(agent)}</span>
                      {record?.role === 'owner' && (
                        <Star className="h-3 w-3 shrink-0 fill-amber-400 text-amber-500" aria-label="Owner" />
                      )}
                    </span>
                    {agent.email && (
                      <span className="block truncate text-[11px] text-slate-500">{agent.email}</span>
                    )}
                  </span>
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      isAssigned ? 'border-brand bg-brand text-white' : 'border-slate-300 bg-white'
                    }`}
                    aria-hidden="true"
                  >
                    {isAssigned && <Check className="h-3 w-3" />}
                  </span>
                </button>
              );
            })}
          </div>

          <p className="border-t border-slate-100 px-3 py-2 text-[10px] font-semibold leading-relaxed text-slate-400">
            Tick as many teammates as you need — the first one is the owner. Changes save immediately.
          </p>
        </div>,
        document.body,
      )}
    </div>
  );
}
